import React, { useState } from 'react';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { MapPin, Phone, Mail, Clock, Send, Loader2 } from 'lucide-react';
import { companyInfo } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const { toast } = useToast();
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    product: '',
    quantity: '',
    deliveryLocation: '',
    additionalInfo: ''
  });

  const [contactLoading, setContactLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);

    try {
      const response = await axios.post(`${API}/contact`, contactForm);
      
      if (response.data.success) {
        toast({
          title: "Message Sent!",
          description: response.data.message,
        });
        setContactForm({ name: '', email: '', phone: '', company: '', message: '' });
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setContactLoading(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setQuoteLoading(true);

    try {
      const response = await axios.post(`${API}/quote`, quoteForm);
      
      if (response.data.success) {
        toast({
          title: "Quote Request Received!",
          description: `${response.data.message} Reference: ${response.data.referenceNumber}`,
        });
        setQuoteForm({
          name: '',
          email: '',
          phone: '',
          company: '',
          industry: '',
          product: '',
          quantity: '',
          deliveryLocation: '',
          additionalInfo: ''
        });
      }
    } catch (error) {
      console.error('Quote request submission error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to submit quote request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setQuoteLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50 pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="mb-4 bg-emerald-600 text-white text-base px-4 py-1">Get in Touch</Badge>
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Contact Us</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ready to partner with Southern Africa's trusted fuel supplier? We're here to help.
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Two Address Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {companyInfo.addresses.map((addr, idx) => (
              <Card key={idx} className="border-emerald-200 hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <MapPin className="h-10 w-10 text-emerald-600 flex-shrink-0" />
                    <div>
                      <CardTitle className="text-lg">{addr.label}</CardTitle>
                      <CardDescription className="text-gray-700 mt-2">
                        {addr.street}
                        {addr.area && <><br />{addr.area}</>}
                        <br />{addr.city}, {addr.province}
                        {addr.postalCode && <><br />{addr.postalCode}</>}
                        <br />{addr.country}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>

          {/* Phone, Email, Hours */}
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-teal-200 hover:shadow-lg transition-all duration-300">
              <CardHeader className="text-center">
                <Phone className="h-10 w-10 text-teal-600 mx-auto mb-3" />
                <CardTitle className="text-lg">Phone</CardTitle>
                <CardDescription className="text-gray-700">
                  {companyInfo.phone.map((num, idx) => (
                    <div key={idx}>{num}</div>
                  ))}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-cyan-200 hover:shadow-lg transition-all duration-300">
              <CardHeader className="text-center">
                <Mail className="h-10 w-10 text-cyan-600 mx-auto mb-3" />
                <CardTitle className="text-lg">Email</CardTitle>
                <CardDescription className="text-gray-700">
                  {companyInfo.email}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-green-200 hover:shadow-lg transition-all duration-300">
              <CardHeader className="text-center">
                <Clock className="h-10 w-10 text-green-600 mx-auto mb-3" />
                <CardTitle className="text-lg">Business Hours</CardTitle>
                <CardDescription className="text-gray-700">
                  Mon - Fri: 8:00 - 17:00<br />
                  Sat: 9:00 - 13:00<br />
                  24/7 Emergency Support
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <Tabs defaultValue="contact" className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12">
              <TabsTrigger value="contact">General Inquiry</TabsTrigger>
              <TabsTrigger value="quote">Request Quote</TabsTrigger>
            </TabsList>

            {/* Contact Form */}
            <TabsContent value="contact">
              <Card className="border-2 border-gray-200">
                <CardHeader>
                  <CardTitle className="text-2xl">Send Us a Message</CardTitle>
                  <CardDescription>
                    Have a question? Fill out the form below and we'll get back to you promptly.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleContactSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="contact-name">Full Name *</Label>
                        <Input
                          id="contact-name"
                          placeholder="John Doe"
                          value={contactForm.name}
                          onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact-email">Email *</Label>
                        <Input
                          id="contact-email"
                          type="email"
                          placeholder="john@company.com"
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="contact-phone">Phone Number</Label>
                        <Input
                          id="contact-phone"
                          placeholder="+27 12 345 6789"
                          value={contactForm.phone}
                          onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact-company">Company Name</Label>
                        <Input
                          id="contact-company"
                          placeholder="Your Company"
                          value={contactForm.company}
                          onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contact-message">Message *</Label>
                      <Textarea
                        id="contact-message"
                        placeholder="Tell us how we can help you..."
                        rows={6}
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        required
                      />
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white transition-all duration-300"
                      disabled={contactLoading}
                    >
                      {contactLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Quote Form */}
            <TabsContent value="quote">
              <Card className="border-2 border-emerald-200">
                <CardHeader>
                  <CardTitle className="text-2xl">Request a Quote</CardTitle>
                  <CardDescription>
                    Get competitive pricing for your fuel requirements. Fill in the details below.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleQuoteSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="quote-name">Full Name *</Label>
                        <Input
                          id="quote-name"
                          placeholder="John Doe"
                          value={quoteForm.name}
                          onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="quote-email">Email *</Label>
                        <Input
                          id="quote-email"
                          type="email"
                          placeholder="john@company.com"
                          value={quoteForm.email}
                          onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="quote-phone">Phone Number *</Label>
                        <Input
                          id="quote-phone"
                          placeholder="+27 12 345 6789"
                          value={quoteForm.phone}
                          onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="quote-company">Company Name *</Label>
                        <Input
                          id="quote-company"
                          placeholder="Your Company"
                          value={quoteForm.company}
                          onChange={(e) => setQuoteForm({ ...quoteForm, company: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="quote-industry">Industry *</Label>
                        <Select
                          value={quoteForm.industry}
                          onValueChange={(value) => setQuoteForm({ ...quoteForm, industry: value })}
                          required
                        >
                          <SelectTrigger id="quote-industry">
                            <SelectValue placeholder="Select your industry" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="logistics">Logistics & Transportation</SelectItem>
                            <SelectItem value="mining">Mining</SelectItem>
                            <SelectItem value="farming">Agriculture & Farming</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="quote-product">Product Required *</Label>
                        <Select
                          value={quoteForm.product}
                          onValueChange={(value) => setQuoteForm({ ...quoteForm, product: value })}
                          required
                        >
                          <SelectTrigger id="quote-product">
                            <SelectValue placeholder="Select product" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="diesel50">Diesel 50ppm</SelectItem>
                            <SelectItem value="diesel10">Diesel 10ppm</SelectItem>
                            <SelectItem value="petrol95">Petrol ULP95</SelectItem>
                            <SelectItem value="petrol93">Petrol ULP93</SelectItem>
                            <SelectItem value="jetfuel">Jet Fuel</SelectItem>
                            <SelectItem value="paraffin">Paraffin</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="quote-quantity">Estimated Quantity (Liters) *</Label>
                        <Input
                          id="quote-quantity"
                          placeholder="e.g., 10000"
                          value={quoteForm.quantity}
                          onChange={(e) => setQuoteForm({ ...quoteForm, quantity: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="quote-location">Delivery Location *</Label>
                        <Input
                          id="quote-location"
                          placeholder="City, Country"
                          value={quoteForm.deliveryLocation}
                          onChange={(e) => setQuoteForm({ ...quoteForm, deliveryLocation: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="quote-info">Additional Information</Label>
                      <Textarea
                        id="quote-info"
                        placeholder="Delivery frequency, contract duration, special requirements..."
                        rows={4}
                        value={quoteForm.additionalInfo}
                        onChange={(e) => setQuoteForm({ ...quoteForm, additionalInfo: e.target.value })}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white transition-all duration-300"
                      disabled={quoteLoading}
                    >
                      {quoteLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          Request Quote
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default Contact;